import React, { useEffect, useState } from 'react';
import type { OrderItem } from '../../../types/apiTypes';
import { useUserSettings } from '../../../contexts/UserSettingsContext';
import { preloadVariantImages } from '../../../services/inventoryCountsAPI';
import { normalizeSku } from '../../../utils/normalizeSku';
import { getSortedItems, hasLocatedParentShelf, isShelfLookupPending } from './shelfUtils';
import ComponentPickList from './ComponentPickList';
import PickLineChip from './PickLineChip';
import ChildConnectorElbow from '../../shared/ChildConnectorElbow';

interface OrderItemsListProps {
  readonly orderItems: readonly OrderItem[];
  readonly pickListComponentsOnly: boolean;
  readonly skusWithNotes: Readonly<Record<string, true>>;
  readonly observeItem: (element: HTMLElement | null, id: string) => void;
  readonly onSelectItem: (item: OrderItem) => void;
  readonly onSelectShelf: (item: OrderItem) => void;
  readonly shelfScanEnabled: boolean;
  readonly onModify?: (item: OrderItem) => void;
  /** Clears manual collapse overrides when the signed-in order changes. */
  readonly orderScope?: string;
}

function defaultBundleCollapsed(item: OrderItem, collapseLocatedBundles: boolean): boolean {
  if (!collapseLocatedBundles) return false;
  return isShelfLookupPending(item.shelfIndex) || hasLocatedParentShelf(item.shelfIndex);
}

function parentIdsWithComponents(items: readonly OrderItem[]): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    if (item.isComponent && item.parentId) {
      ids.add(item.parentId);
    }
  }
  return ids;
}

function OrderItemsList({
  orderItems,
  pickListComponentsOnly,
  skusWithNotes,
  observeItem,
  onSelectItem,
  onSelectShelf,
  shelfScanEnabled,
  onModify,
  orderScope,
}: OrderItemsListProps): React.ReactElement {
  const { settings } = useUserSettings();
  const [collapseOverrides, setCollapseOverrides] = useState<Readonly<Record<string, boolean>>>({});

  useEffect(() => {
    setCollapseOverrides({});
  }, [orderScope]);

  useEffect(() => {
    const skus: string[] = [];
    for (const item of orderItems) {
      if (item.isComponent && item.sku) skus.push(item.sku);
    }
    preloadVariantImages(skus);
  }, [orderItems]);

  if (pickListComponentsOnly) {
    return (
      <ComponentPickList
        orderItems={orderItems}
        skusWithNotes={skusWithNotes}
        observeItem={observeItem}
        onSelectItem={onSelectItem}
        onSelectShelf={onSelectShelf}
        shelfScanEnabled={shelfScanEnabled}
        onModify={onModify}
      />
    );
  }

  const sortedItems = getSortedItems([...orderItems]);
  const parentsWithComponents = parentIdsWithComponents(sortedItems);
  const collapsedParentIds = new Set(
    sortedItems
      .filter((item) => parentsWithComponents.has(item.id))
      .filter((item) => {
        const override = collapseOverrides[item.id];
        if (override !== undefined) return override;
        return defaultBundleCollapsed(item, settings.collapseLocatedBundles);
      })
      .map((item) => item.id),
  );
  const toggleParent = (item: OrderItem): void => {
    setCollapseOverrides((prev) => {
      const current = prev[item.id] ?? defaultBundleCollapsed(item, settings.collapseLocatedBundles);
      return { ...prev, [item.id]: !current };
    });
  };

  const rows: React.ReactElement[] = [];
  let index = 0;
  while (index < sortedItems.length) {
    const item = sortedItems[index];
    if (item.isComponent) {
      const orphans: OrderItem[] = [];
      while (index < sortedItems.length && sortedItems[index].isComponent) {
        orphans.push(sortedItems[index]);
        index += 1;
      }
      orphans.forEach((orphan, orphanIndex) => {
        rows.push(renderPickLine({
          item: orphan,
          observeItem,
          skusWithNotes,
          onSelectItem,
          onSelectShelf,
          shelfScanEnabled,
          onModify,
          isFirstComponent: orphanIndex === 0,
          isLastComponent: orphanIndex === orphans.length - 1,
        }));
      });
      continue;
    }

    const components: OrderItem[] = [];
    let nextIndex = index + 1;
    while (
      nextIndex < sortedItems.length
      && sortedItems[nextIndex].isComponent
      && sortedItems[nextIndex].parentId === item.id
    ) {
      components.push(sortedItems[nextIndex]);
      nextIndex += 1;
    }

    const canCollapse = components.length > 0;
    const collapsed = canCollapse && collapsedParentIds.has(item.id);
    const hasWarehouseNote = Boolean(item.sku && skusWithNotes[normalizeSku(item.sku)]);

    rows.push(
      <li
        key={item.id}
        ref={(el) => observeItem(el, item.id)}
        className={`item${canCollapse ? ' bundle-parent' : ''}${item.scanned ? ' scanned' : ''}`}
      >
        <PickLineChip
          item={item}
          hasWarehouseNote={hasWarehouseNote}
          onSelectProduct={onSelectItem}
          onSelectShelf={onSelectShelf}
          shelfScanEnabled={shelfScanEnabled}
          onModify={onModify}
          componentsCollapsed={canCollapse ? collapsed : undefined}
          onToggleComponents={canCollapse ? () => toggleParent(item) : undefined}
        />
        {canCollapse ? (
          <div className={`bundle-components${collapsed ? ' is-collapsed' : ''}`}>
            <div className="bundle-components-inner">
              <ul className="bundle-components-list">
                {components.map((component, componentIndex) => renderPickLine({
                  item: component,
                  observeItem,
                  skusWithNotes,
                  onSelectItem,
                  onSelectShelf,
                  shelfScanEnabled,
                  onModify,
                  isFirstComponent: componentIndex === 0,
                  isLastComponent: componentIndex === components.length - 1,
                }))}
              </ul>
            </div>
          </div>
        ) : null}
      </li>,
    );
    index = nextIndex;
  }

  return (
    <div className="items-container">
      <ul className="items-list">
        {rows}
      </ul>
    </div>
  );
}

interface PickLineRenderProps {
  readonly item: OrderItem;
  readonly observeItem: (element: HTMLElement | null, id: string) => void;
  readonly skusWithNotes: Readonly<Record<string, true>>;
  readonly onSelectItem: (item: OrderItem) => void;
  readonly onSelectShelf: (item: OrderItem) => void;
  readonly shelfScanEnabled: boolean;
  readonly onModify?: (item: OrderItem) => void;
  readonly isFirstComponent: boolean;
  readonly isLastComponent: boolean;
}

function renderPickLine({
  item,
  observeItem,
  skusWithNotes,
  onSelectItem,
  onSelectShelf,
  shelfScanEnabled,
  onModify,
  isFirstComponent,
  isLastComponent,
}: PickLineRenderProps): React.ReactElement {
  const hasWarehouseNote = Boolean(item.sku && skusWithNotes[normalizeSku(item.sku)]);
  return (
    <li
      key={item.id}
      ref={(el) => observeItem(el, item.id)}
      className={`item${item.scanned ? ' scanned' : ''}${
        item.isComponent ? ' component-item' : ''
      }${isFirstComponent ? ' component-first' : ''}${isLastComponent ? ' component-last' : ''}`}
    >
      {item.isComponent ? (
        <ChildConnectorElbow
          className={`item-connector-svg${isFirstComponent ? ' connector-first' : ''}${isLastComponent ? ' connector-last' : ''}`}
        />
      ) : null}
      <PickLineChip
        item={item}
        hasWarehouseNote={hasWarehouseNote}
        onSelectProduct={onSelectItem}
        onSelectShelf={onSelectShelf}
        shelfScanEnabled={shelfScanEnabled}
        onModify={onModify}
      />
    </li>
  );
}

export default OrderItemsList;
